import * as ss from 'superstruct';
import type { AdAccountPlatform } from '../../../ad-platforms/ad-account-platform';
import { IntegrationConnectionType } from '../../../connected-accounts';
import { ConversionSource } from '../../conversion-source';
import {
  ConversionInputStreamSource,
  ConversionInputStreamSourceList,
} from './source-list';

type InputStreamSourceInfo = {
  conversionSource: ConversionSource;
  conversionEventType: string;
  connection?: IntegrationConnectionType;
  /** The platform of the ad account it reads from, if any. */
  adAccount?: AdAccountPlatform;
};

const InputStreamSourceName = ss.enums(ConversionInputStreamSourceList);

/**
 * Hardcoded config for each input stream source: the conversionSource and
 * conversionEventType it produces, and the type of connection or the ad
 * account it reads through, if any. Credentials live on connections, never in
 * the source.
 */
export const inputStreamSourceConfig: Record<
  ConversionInputStreamSource,
  InputStreamSourceInfo
> = {
  dialpad: {
    conversionSource: 'phone-call',
    conversionEventType: 'dialpad-call',
    connection: 'dialpad',
  },
  'leap-lead': {
    conversionSource: 'crm',
    conversionEventType: 'leap-lead',
    connection: 'leap',
  },
  'leap-appointment-set': {
    conversionSource: 'crm',
    conversionEventType: 'leap-appointment-set',
    connection: 'leap',
  },
  'leap-job-created': {
    conversionSource: 'crm',
    conversionEventType: 'leap-job-created',
    connection: 'leap',
  },
  'leap-job-awarded': {
    conversionSource: 'crm',
    conversionEventType: 'leap-job-awarded',
    connection: 'leap',
  },
  'google-lsa-lead': {
    conversionSource: 'google',
    conversionEventType: 'google-lsa-lead',
    // Reads through our global Google Ads credentials.
    adAccount: 'google',
  },
};

const configFor = (sourceName: string): InputStreamSourceInfo | undefined =>
  ss.is(sourceName, InputStreamSourceName)
    ? inputStreamSourceConfig[sourceName]
    : undefined;

/** The type of connection streams from `sourceName` read through, if any. */
export const inputStreamConnectionType = (
  sourceName: string,
): IntegrationConnectionType | undefined => configFor(sourceName)?.connection;

/** The platform of the ad account streams from `sourceName` read, if any. */
export const inputStreamAdAccountPlatform = (
  sourceName: string,
): AdAccountPlatform | undefined => configFor(sourceName)?.adAccount;
